import {logger} from 'src/utils/logger';

/**
 * This module is used when the payin flow is opened in a pop-up window.
 * It allows communications with the pop-up opener (aka the merchant
 * site) via postMessage. The opener sends 'in_context_connect' with a MessagePort; we store it as inContextPort.
 * When the payin flow finishes we call notifyInContextComplete() to send 'in_context_complete' so the opener
 * can close the pop-up and transition to the next step in the flow.
 * Refer to stripe-js-v3/src/inContext/shared/popupWindowManager.ts for port transfer and completion handler.
 */
let inContextPort: MessagePort | null = null;

export function getInContextPort(): MessagePort | null {
  return inContextPort;
}

export function setInContextPort(port: MessagePort | null): void {
  inContextPort = port;
}

export function notifyInContextComplete(): boolean {
  if (!inContextPort) {
    return false;
  }
  try {
    inContextPort.postMessage({event: 'in_context_complete'});
    return true;
  } catch (e) {
    logger.log('crypto_payin_session.error', {
      error_code: 'in_context_complete_post_message_failed',
      error_message: e instanceof Error ? e.message : String(e),
    });
    return false;
  } finally {
    inContextPort = null;
  }
}

export function setupInContextMessageListener(): () => void {
  const handler = (event: MessageEvent) => {
    // When the popup is opened from Outer code, the opener is the merchant site, so
    // event.origin can be the merchant origin. We cannot blacklist origins
    // without blocking legitimate flows. A malicious actor could open this
    // popup and obtain the MessagePort; we accept that risk.
    if (
      event.data === 'in_context_connect' &&
      event.ports &&
      event.ports.length > 0
    ) {
      setInContextPort(event.ports[0]);
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
